<template>
  <layout>
    <page-header :actions="actions">
      <template slot="header">
        <h4 class="mr-4">
          <i class="icon-coins mr-2"></i>
          {{ $t('orders.title') }}
        </h4>
        <b-nav pills class="mb-0">
          <b-nav-item
            v-for="(tab, key) in tabs"
            :key="key"
            :active="key === currentTab"
            link-classes="py-1"
            @click="setTab(key)"
          >
            {{ tab.label }}
            <b-badge v-if="tab.count" variant="warning">
              {{ tab.count }}
            </b-badge>
          </b-nav-item>
        </b-nav>
      </template>
    </page-header>
    <div class="content">
      <admin-table
        v-if="currentDivision"
        ref="table"
        v-bind="tableProps"
        @api-result="itemsLoaded"
      >
        <template slot="user" slot-scope="{ item }">
          <user-link v-if="item.user" :user="item.user" />
          <div v-else>
            <system-user />
          </div>
        </template>
        <template slot="recipient" slot-scope="{ item }">
          <user-link v-if="item.recipient" :user="item.recipient" />
          <div v-else>
            <system-user />
          </div>
        </template>
        <template slot="amount" slot-scope="{ item }">
          <order-final-amount :order="item" />
        </template>
        <template slot="status" slot-scope="{ item }">
          <order-status :order="item" class="mr-1" />
          <value-payment-method :item="item" />
        </template>
      </admin-table>
    </div>

    <OrderCompleteModal
      id="order-complete-modal"
      ref="orderCompleteModal"
      :busy="busy"
      :payment-methods="paymentMethods"
      :title="$t('orders.complete_modal_title')"
      :selected-method="selectedMethod"
      @confirm="complete"
      @hide="hideCompleteModal"
    ></OrderCompleteModal>
  </layout>
</template>

<script>
import { AdminTable } from 'stylemix-vue-admin'
import { create } from 'vue-modal-dialogs'
import OrdersApi from './Api'
import SystemUser from '../components/SystemUser'
import View from './View'
import ValuePaymentMethod from '../components/values/ValuePaymentMethod'
import OrderStatus from './OrderStatus'
import UserLink from '../components/UserLink'
import OrderFinalAmount from './OrderFinalAmount'
import OrderCompleteModal from './OrderCompleteModal.vue'

const resourceBase = 'orders'

export default {
  name: 'Orders',

  components: {
    AdminTable,
    OrderCompleteModal,
    OrderFinalAmount,
    OrderStatus,
    SystemUser,
    UserLink,
    ValuePaymentMethod,
  },

  data() {
    return {
      // Table resource configuration
      tableProps: {
        base: resourceBase,
        apiBuilder: () => {
          const parameters = {
            include: 'user,recipient',
            division_id: this.currentDivision.id,
          }

          switch (this.currentTab) {
            case 'completed':
              parameters['where[status]'] = 'completed'
              break
            case 'unpaid':
              parameters['where[status]'] = 'partial,pending'
              break
            case 'cancelled':
              parameters['where[status]'] = 'cancelled'
              break
            case 'refund_requests':
              parameters['where[refund_requested]'] = 'true'
              break
            case 'refunds':
              parameters['where[status]'] = 'refunding,partial_refund,refunded'
              break
            case 'affiliates_only':
              parameters['where[affiliates_only]'] = 'true'
              break
            case 'partners_only':
              parameters['where[partners_only]'] = 'true'
              parameters.division_id = null
              break
            case 'commissions_only':
              parameters['where[commissions_only]'] = 'true'
              parameters.division_id = null
              break
          }

          return new OrdersApi().setParameters(parameters)
        },
        sortBy: 'created_at',
        sortDesc: true,
        perPage: 15,
        columns: [
          {
            key: 'invoice_number',
            label: this.$t('orders.number'),
            formatter: value => `#${value}`,
            sortable: true,
          },
          {
            key: 'description',
            label: this.$t('orders.description'),
            sortable: true,
          },
          {
            key: 'user',
            label: this.$t('orders.payee'),
          },
          {
            key: 'recipient',
            label: this.$t('orders.recipient'),
          },
          {
            key: 'amount',
            label: this.$t('orders.amount'),
            sortable: true,
          },
          {
            key: 'status',
            label: this.$t('general.status'),
            sortable: true,
          },
          {
            key: 'completed_at',
            label: this.$t('orders.completed'),
            sortable: true,
            component: 'value-date',
            config: {
              stacked: true,
            },
          },
          {
            key: 'created_at',
            label: this.$t('orders.created'),
            sortable: true,
            component: 'value-date',
            config: {
              stacked: true,
            },
          },
          // define column fields here
        ],

        // override search fields here using fields schema
        searchFields: this.searchFields(),
        searchData: {
          where: {
            query: null,
            created_at: null,
            payment_method: null,
          },
        },

        // define custom row actions
        rowActions: item => {
          const actions = [
            {
              label: this.$t('general.view'),
              variant: 'light',
              handler: () => {
                this.viewDialog(item)
              },
            },
          ]

          let allowComplete = item.status === 'pending' && this.$allows('orders.complete')

          if (this.$allows('orders.update')) {
            actions.push('edit')

            if (item.category === 'payout' && item.status === 'pending') {
              allowComplete = true
            } else if (
              item.category === 'commission' &&
              item.status === 'pending' &&
              item.recipient === null
            ) {
              allowComplete = true
          }

          if (allowComplete) {
              actions.push({
                label: this.$t('orders.complete'),
                variant: 'success',
                handler: () => {
                  this.showCompleteModal(item)
                },
              })
            }
          }

          return actions
        },

        // define custom bulk actions
        bulkActions() {
          return []
        },
      },
      busy: false,
      currentTab: 'all',
      refundRequests: null,
      paymentMethods: [],
      selectedOrder: null,
    }
  },

  computed: {
    // Page header actions
    actions() {
      if (this.$allows(`${resourceBase}.create`)) {
        return [
          {
            id: 'create',
            text: this.$t('general.add_new'),
            icon: 'icon-plus-circle2',
            onClick: () => {
              // eslint-disable-next-line vue/no-side-effects-in-computed-properties
              this.$router.push({
                name: `${resourceBase}.create`,
              })
            },
          },
        ]
      }

      return []
    },
    tabs() {
      const tabs = {
        all: {
          label: this.$t('orders.all_statuses'),
        },
        completed: {
          label: this.$t('orders.completed'),
        },
        unpaid: {
          label: this.$t('orders.unpaid'),
        },
        cancelled: {
          label: this.$t('orders.cancelled'),
        },
        refund_requests: {
          label: this.$t('orders.refund_requests'),
          count: this.refundRequests,
        },
        refunds: {
          label: this.$t('orders.refunds'),
        },
      }
      if (this.$allows('administer')) {
        tabs.affiliates_only = {
          label: this.$t('orders.affiliates_only'),
        }
        tabs.partners_only = {
          label: this.$t('orders.partners_only'),
        }
        tabs.commissions_only = {
          label: this.$t('orders.commissions_only'),
        }
      }
      return tabs
    },

    selectedMethod() {
      return this.selectedOrder
        ? this.selectedOrder.payment_method
        : this.paymentMethods[0] || ''
    },
  },

  watch: {
    currentDivision() {
      this.$refs.table && this.$refs.table.reset()
    },
  },

  created() {
    this.viewDialog = create(View, 'order')
  },

  methods: {
    setTab(key) {
      this.currentTab = key
      this.$refs.table.reset()
    },
    itemsLoaded(result) {
      this.refundRequests = result.refund_requests
      this.paymentMethods = result.payment_methods
    },
    searchFields() {
      const fields = [
        {
          component: 'text-field',
          type: 'search',
          attribute: 'where.query',
          placeholder: this.$t('orders.search'),
        },
        {
          component: 'date-range-field',
          attribute: 'where.created_at',
          placeholder: this.$t('orders.creation_date_period'),
        },
      ]

      if (this.$allows('administer')) {
        fields.push({
          component: 'select-field',
          attribute: 'where.payment_method',
          placeholder: this.$t('orders.payment_method'),
          options: [
            {
              value: '',
              label: this.$t('enums.payment_method.all'),
            },
            {
              value: 'cash',
              label: this.$t('enums.payment_method.cash'),
            },
            {
              value: 'card',
              label: this.$t('enums.payment_method.card'),
            },
            {
              value: 'bancontact',
              label: this.$t('enums.payment_method.bancontact'),
            },
            {
              value: 'on_invoice',
              label: this.$t('enums.payment_method.on_invoice'),
            },
          ],
        })
      }
      return fields
    },

    complete(paymentMethod) {
      this.busy = true
      return new OrdersApi()
        .complete(this.selectedOrder, {
          payment_method: paymentMethod,
        })
        .then(result => {
          this.$toast.success(result.message)
          this.hideCompleteModal()
          this.$refs.table.refresh()
        })
        .catch(response => {
          this.$toast.error(response.data.message)
        })
        .finally(() => {
          this.busy = false
        })
    },

    showCompleteModal(order) {
      this.selectedOrder = order
      this.$bvModal.show('order-complete-modal')
    },

    hideCompleteModal() {
      this.selectedOrder = null
      this.$bvModal.hide('order-complete-modal')
    },
  },
}
</script>
